<template>
  <div>
    <view-dependencies
      :is-collection="!!collection"
      :collection="collection"
      :game="game"
      :dependencies="
        [...sortedMods, ...hiddenMods].slice(
          startIndex,
          startIndex + PAGE_LIMIT
        )
      "
      :loading="loading"
    >
      <div class="tw-flex tw-items-center tw-justify-between tw-gap-6">
        <loading-title v-if="loading" />
        <div v-else class="mod-text-p-md">
          <template v-if="collection">
            {{ modsTotal }} {{ $t('available') }}
            <base-dot-divide class="tw-align-middle tw-mr-2" />
            {{ hiddenModCount }} {{ $t('unavailable') }}
          </template>
          <template v-else>
            {{
              $t('mod_dependencies', { ugc: ugcName(game, true, true).value })
            }}
          </template>
        </div>

        <sort-bar
          :id="modListId"
          :right="true"
          class="md:tw-w-52"
          :sort-options="sortOptions"
          :input-loading="loading"
          @update:sort="handleSort"
        />
      </div>
    </view-dependencies>

    <pagination-container
      :id="paginationId"
      :limits="false"
      :loading="loading"
    />
  </div>
</template>
<script>
import { COLLECTION_MODS_SORT_OPTIONS as sortOptions } from '@config/sort.js'
import PaginationContainer from '@components/List/PaginationContainer.vue'
import { useResourceValue, usePagination, useSort } from '@composables'
import ViewDependencies from '@components/Mod/ViewDependencies.vue'
import LoadingTitle from '@components/Loading/LoadingTitle.vue'
import BaseDotDivide from '@components/Misc/BaseDotDivide.vue'
import SortBar from '@components/List/SortBar.vue'
import { computed, toRefs, watch, ref } from 'vue'
import i18n from '@plugins/localisation'
import { useI18n } from 'vue-i18n'

const PAGE_LIMIT = 25
const modListId = 'view-mod-list'

export default {
  components: {
    PaginationContainer,
    ViewDependencies,
    BaseDotDivide,
    LoadingTitle,
    SortBar,
  },
  props: {
    game: {
      type: Object,
      default: () => {},
    },
    modList: {
      type: Array,
      default: () => [],
    },
    collection: {
      type: Object,
      default: null,
    },
    mod: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modsTotal: {
      type: Number,
      default: 0,
    },
    hiddenModCount: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const { mod, modList, game, collection, modsTotal, hiddenModCount } =
      toRefs(props)

    const sortedMods = ref([])
    const { ugcName } = useResourceValue()
    const { inMemorySort, setSort, sort } = useSort(
      modListId,
      '-downloads_total',
      sortOptions
    )

    const { t } = useI18n()
    const hiddenMods = ref([])

    const paginationId = computed(
      () =>
        `${game.value?.name_id}-${collection.value ? collection.value.name_id : mod.value?.name_id}`
    )

    const { setPagination, pagination } = usePagination(paginationId.value, {
      limit: PAGE_LIMIT,
    })

    const startIndex = computed(() => PAGE_LIMIT * (pagination.value.page - 1))

    watch(
      [modList, hiddenModCount, i18n.global.locale, sort],
      ([, _hiddenModCount]) => {
        setPagination({
          page: pagination.value.page,
          limit: PAGE_LIMIT,
          total: modsTotal.value + _hiddenModCount,
        })

        hiddenMods.value = []
        sortedMods.value = inMemorySort({ data: modList.value })

        for (let i = 0; i < _hiddenModCount; i++) {
          hiddenMods.value.push({
            id: `hidden-${i}`,
            name: t('mod_not_available', {
              ugc: ugcName(game, true, true).value,
            }),
            hidden: true,
            name_id: `hidden-${i}`,
          })
        }
      },
      {
        immediate: true,
      }
    )

    function handleSort(update) {
      setSort({
        sortBy: update.key,
        alias: update.alias,
        direction: update.direction,
      })
    }

    return {
      paginationId,
      sortOptions,
      sortedMods,
      hiddenMods,
      PAGE_LIMIT,
      startIndex,
      handleSort,
      modListId,
      ugcName,
    }
  },
}
</script>
